import {
  isBroadcastMessage,
  type AnyMessage,
  type BroadcastMessage,
  type Message,
  AnyPayload,
  MessageWithType,
} from '../types';
import UUID from '../utils/UUID';

export const MSG_FORMAT_HS_IFR = '__hs-ifr';
export const MSG_FORMAT_VERSION = 1;
export const TO_ALL = 'ALL';

export const isOfType = <T extends string>(
  message: AnyMessage,
  type: T
): message is MessageWithType<T> => message.payload.type === type;

export const validFormat = (eventData: any): eventData is AnyMessage => {
  const { format, v } = eventData || {};
  return format === MSG_FORMAT_HS_IFR && v > 0 && v <= MSG_FORMAT_VERSION;
};

interface Channel {
  from: string;
  to: string;
}

interface GroupChannel {
  from: string;
  group: string;
}

export const createMessage = <
  const T extends string,
  P extends AnyPayload = AnyPayload,
>(
  type: T,
  data: P,
  { to, from }: Channel
): MessageWithType<T, P> => ({
  id: UUID(),
  format: MSG_FORMAT_HS_IFR,
  from,
  to,
  v: MSG_FORMAT_VERSION,
  payload: {
    type,
    ...data,
  },
});

export const createReplyMessage = (
  replyToMessage: AnyMessage,
  type: string,
  data?: AnyPayload
): Message => ({
  ...createMessage(type, data, {
    from: replyToMessage.to,
    to: replyToMessage.from,
  }),
  id: replyToMessage.id,
});

export const createDirectedMessage = (
  type: string,
  data: AnyPayload,
  { to, from }: Channel
) => createMessage(type, data, { to, from });

export const createDirectedReplyMessage = createReplyMessage;

export const createBroadcastMessage = (
  type: string,
  data: AnyPayload,
  { from, group }: GroupChannel
): BroadcastMessage => ({
  ...createMessage(type, data, { to: TO_ALL, from }),
  group,
});

export const createBroadcastReplyMessage = (
  replyToMessage: BroadcastMessage,
  type: string,
  data: AnyPayload,
  from: string
) => ({
  ...createReplyMessage(replyToMessage, type, data),
  from,
});

export const isBroadcastMessageToMyGroup = (
  message: BroadcastMessage,
  group: string | undefined
): boolean => message.to === TO_ALL && message.group === group;

export const isMessageToMe = (
  message: AnyMessage,
  myId: string,
  myGroup: string | undefined
): boolean =>
  message.to === myId ||
  (isBroadcastMessage(message) &&
    isBroadcastMessageToMyGroup(message, myGroup));
